/**
 * EstimateHeadingPicker — PRP-MCP-15 (F3, F4, DD-15.4) on the estimate page: which of
 * the user's headings this estimate exports with ("None" included), and an "Edit for
 * this estimate" panel that changes its statements or estimator without changing the
 * saved heading, with an explicit "Save to heading". A chosen heading that was deleted
 * is said plainly: the estimate exports with no heading, never another one.
 * Reads/writes /me/headings/estimate/{parseHash} (headingsApi.ts).
 */
import { useEffect, useState } from 'react';

import { HeadingsAPI, NO_HEADING } from '../../lib/headingsApi';
import type { CompanyEstimator, EstimateHeading, Heading, HeadingOverrides } from '../../lib/headingsApi';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select } from './ui/select';
import { Textarea } from './ui/textarea';
import { ESTIMATOR_FIELDS, FieldLabel, errorText } from './HeadingsSection';
import HeadingsBoundary from './HeadingsBoundary';
import { estimateHeading, headingList } from '../../lib/headingsShape';

type StatementKey = 'opening_statement' | 'closing_statement';
const FOLLOW_DEFAULT = '';
const MISSING = '__missing__';

/** Shown instead of the picker when headings cannot be read; the rest of the page is untouched. */
function Unavailable() {
  return (
    <span className="text-[11px] text-[var(--text-secondary)]" data-testid="estimate-heading-unavailable">
      Headings unavailable. The ESX still downloads.
    </span>
  );
}

export default function EstimateHeadingPicker({ parseHash }: { parseHash: string }) {
  return (
    <HeadingsBoundary key={parseHash} fallback={<Unavailable />}>
      <Picker parseHash={parseHash} />
    </HeadingsBoundary>
  );
}

function Picker({ parseHash }: { parseHash: string }) {
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [view, setView] = useState<EstimateHeading | null>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Partial<Record<StatementKey, string>>>({});
  const [estDraft, setEstDraft] = useState<CompanyEstimator>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([HeadingsAPI.list(), HeadingsAPI.forEstimate(parseHash)])
      .then(([list, v]) => {
        if (cancelled) return;
        setHeadings(headingList(list).headings);
        setView(estimateHeading(v));
      })
      .catch(() => {
        if (!cancelled) setUnavailable(true);
      });
    return () => {
      cancelled = true;
    };
  }, [parseHash]);

  if (!view) return unavailable ? <Unavailable /> : null;

  const def = headings.find((h) => h.is_default) ?? null;
  const selected = view.state === 'missing' ? MISSING : view.heading_id ?? (def ? FOLLOW_DEFAULT : NO_HEADING);
  const hasOverrides = Boolean(view.overrides.opening_statement || view.overrides.closing_statement
    || view.overrides.estimator);
  const eff = view.effective;
  const value = (k: StatementKey) => draft[k] ?? eff?.[k] ?? '';
  const estValue = (k: keyof CompanyEstimator) => estDraft[k] ?? eff?.estimator?.[k] ?? '';
  const dirty = Object.keys(draft).length > 0 || Object.keys(estDraft).length > 0;

  async function save(work: () => Promise<EstimateHeading>) {
    setBusy(true);
    setError(null);
    try {
      setView(estimateHeading(await work()));
      setDraft({});
      setEstDraft({});
    } catch (e) {
      setError(errorText(e, 'Could not save.'));
    } finally {
      setBusy(false);
    }
  }

  function choose(v: string) {
    setOpen(false);
    void save(() => HeadingsAPI.setForEstimate(parseHash, { heading_id: v === FOLLOW_DEFAULT ? null : v }));
  }

  function applyOverrides() {
    const body: HeadingOverrides = { ...draft };
    if (Object.keys(estDraft).length > 0) body.estimator = { ...(eff?.estimator ?? {}), ...estDraft };
    void save(() => HeadingsAPI.setForEstimate(parseHash, { overrides: body }));
  }

  if (headings.length === 0 && view.state !== 'missing') {
    return (
      <span className="text-[11px] text-[var(--text-secondary)]" data-testid="estimate-heading-none-yet">
        No company heading. Add one in Settings, Estimate headings.
      </span>
    );
  }

  return (
    <div className="flex flex-wrap items-center justify-end gap-2" data-testid="estimate-heading">
      <label htmlFor="estimate-heading-select" className="text-xs text-[var(--text-secondary)]">Heading</label>
      <Select id="estimate-heading-select" className="w-auto min-w-[180px] h-[30px] text-[12px]" value={selected}
        disabled={busy} onChange={(e) => choose(e.target.value)} data-testid="estimate-heading-select">
        {def && <option value={FOLLOW_DEFAULT}>Default ({def.name})</option>}
        <option value={NO_HEADING}>None</option>
        {view.state === 'missing' && <option value={MISSING} disabled>Deleted heading</option>}
        {headings.map((h) => (
          <option key={h.id} value={h.id}>{h.name}</option>
        ))}
      </Select>
      {eff && (
        <Button size="sm" variant="secondary" onClick={() => setOpen((o) => !o)} disabled={busy}
          data-testid="estimate-heading-edit">
          {open ? 'Close' : hasOverrides ? 'Edited for this estimate' : 'Edit for this estimate'}
        </Button>
      )}
      {view.state === 'missing' && (
        <span className="basis-full text-right text-[11px] text-[var(--warn)]" data-testid="estimate-heading-missing">
          The heading this estimate used was deleted. It exports with no heading until you choose one.
        </span>
      )}
      {error && <span className="basis-full text-right text-[11px] text-[var(--bad)]" data-testid="estimate-heading-error">{error}</span>}
      {open && eff && (
        <div className="basis-full space-y-3 text-left border border-[var(--bd)] rounded-[var(--r-md)] p-3"
          data-testid="estimate-heading-panel">
          <p className="m-0 text-[12px] text-[var(--tx-lo)]">
            Changes here apply to this estimate only. "{view.heading?.name}" stays as saved unless you save to it.
          </p>
          {(['opening_statement', 'closing_statement'] as const).map((k) => (
            <div key={k}>
              <FieldLabel htmlFor={`estimate-heading-${k}`}>
                {k === 'opening_statement' ? 'Opening statement' : 'Closing statement'}
              </FieldLabel>
              <Textarea id={`estimate-heading-${k}`} rows={4} value={value(k)} data-testid={`estimate-heading-${k}`}
                onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))} />
            </div>
          ))}
          <div className="grid grid-cols-2 gap-3 max-md:grid-cols-1">
            {ESTIMATOR_FIELDS.map(([k, label]) => (
              <div key={k}>
                <FieldLabel htmlFor={`estimate-heading-est-${k}`}>{label}</FieldLabel>
                <Input id={`estimate-heading-est-${k}`} value={estValue(k)} data-testid={`estimate-heading-est-${k}`}
                  onChange={(e) => setEstDraft((d) => ({ ...d, [k]: e.target.value }))} />
              </div>
            ))}
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            {hasOverrides && (
              <Button size="sm" variant="secondary" disabled={busy} data-testid="estimate-heading-reset"
                onClick={() => void save(() => HeadingsAPI.setForEstimate(parseHash, { overrides: null }))}>
                Use the heading as saved
              </Button>
            )}
            <Button size="sm" variant="secondary" disabled={busy || dirty || !hasOverrides}
              data-testid="estimate-heading-save-to-heading"
              title={dirty ? 'Apply your changes to this estimate first.' : undefined}
              onClick={() => void save(() => HeadingsAPI.saveToHeading(parseHash))}>
              Save to heading
            </Button>
            <Button size="sm" variant="primary" disabled={busy || !dirty} onClick={applyOverrides}
              data-testid="estimate-heading-apply">
              Apply to this estimate
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
